import { z } from 'zod';
import { ApprovalStatus, AttendanceMark, AttendanceRange, BookingStatus, CommunityScope, CommunityTopic, LessonType, Locale, NotificationCategory, PaymentPeriodStatus, PaymentStatus, RoomAvailabilityState, ScheduleEntryKind, ScheduleFilter, SkillLevel, SlotState, StudentScope, StudentStanding, UserRole, } from './enums.js';
export const phoneSchema = z
    .string()
    .trim()
    .min(9, 'validation.phoneRequired')
    .max(20)
    .regex(/^(\+?994|0)?\s?\(?\d{2}\)?[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2}$/, 'validation.phoneInvalid');
export const emailSchema = z.string().trim().toLowerCase().email('validation.email');
export const passwordSchema = z
    .string()
    .min(10, 'validation.passwordMin')
    .max(128, 'validation.tooLong');
export const localeSchema = z.nativeEnum(Locale);
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'validation.date');
export function optional(schema) {
    return z.preprocess((value) => (value === '' || value === null ? undefined : value), schema.optional());
}
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(60).default(12),
});
export const signUpSchema = z.object({
    fullName: z.string().trim().min(2, 'validation.nameRequired').max(120),
    email: emailSchema,
    phone: phoneSchema,
    password: passwordSchema,
    locale: optional(localeSchema),
    whatsappOptIn: z.boolean().optional().default(true),
    acceptsTerms: z.literal(true, {
        error: 'validation.termsRequired',
    }),
});
export const logInSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'validation.passwordRequired'),
    rememberMe: z.boolean().optional().default(false),
});
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z
    .object({
    token: z.string().min(10),
    password: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((v) => v.password === v.confirmPassword, {
    message: 'validation.passwordsMatch',
    path: ['confirmPassword'],
});
export const verifyAccountSchema = z
    .object({
    token: z.string().min(10).optional(),
    code: z
        .string()
        .regex(/^\d{6}$/, 'validation.code')
        .optional(),
    email: emailSchema.optional(),
})
    .refine((v) => Boolean(v.token) || Boolean(v.code && v.email), {
    message: 'validation.tokenOrCode',
});
export const authUserSchema = z.object({
    id: z.string(),
    fullName: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    role: z.nativeEnum(UserRole),
    locale: localeSchema,
    emailVerified: z.boolean(),
    avatarUrl: z.string().nullable(),
    approvalStatus: z.nativeEnum(ApprovalStatus),
});
export const authSessionSchema = z.object({
    user: authUserSchema,
    accessToken: z.string(),
    expiresIn: z.number(),
});
const priceOptionMinorSchema = z.number().int().min(0).max(1_000_000).nullable();
export const subjectPricesSchema = z.object({
    monthlyMinor: priceOptionMinorSchema,
    monthlyDiscountMinor: priceOptionMinorSchema,
    threeMonthMinor: priceOptionMinorSchema,
    onceWeeklyMinor: priceOptionMinorSchema,
    singleMinor: priceOptionMinorSchema,
    group8Minor: priceOptionMinorSchema,
    group4Minor: priceOptionMinorSchema,
    group1Minor: priceOptionMinorSchema,
});
export const SUBJECT_PRICE_FIELDS = [
    'monthlyMinor',
    'monthlyDiscountMinor',
    'threeMonthMinor',
    'onceWeeklyMinor',
    'singleMinor',
    'group8Minor',
    'group4Minor',
    'group1Minor',
];
export const subjectSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    tagline: z.string(),
    description: z.string(),
    icon: z.string(),
    levelLine: z.string(),
    sortOrder: z.number(),
    teacherCount: z.number().optional(),
    prices: subjectPricesSchema,
});
export const teacherSummarySchema = z.object({
    id: z.string(),
    slug: z.string(),
    fullName: z.string(),
    headline: z.string(),
    photoUrl: z.string().nullable(),
    yearsTeaching: z.number(),
    featured: z.boolean(),
    subjects: z.array(z.object({ slug: z.string(), name: z.string() })),
    languages: z.array(z.string()),
    levels: z.array(z.nativeEnum(SkillLevel)),
    nextAvailableAt: z.string().nullable(),
    trialPrice: z.number().nullable(),
    hourlyRate: z.number().nullable(),
});
export const teacherDetailSchema = teacherSummarySchema.extend({
    bio: z.string(),
    approach: z.string(),
    credentials: z.array(z.string()),
    teachesAtHome: z.boolean(),
    testimonials: z.array(z.object({
        id: z.string(),
        quote: z.string(),
        authorName: z.string(),
        authorContext: z.string(),
    })),
});
export const teacherQuerySchema = paginationSchema.extend({
    subject: optional(z.string()),
    level: optional(z.nativeEnum(SkillLevel)),
    language: optional(z.string()),
    day: optional(z.string()),
    q: optional(z.string().trim().max(120)),
    sort: z.enum(['availability', 'experience', 'name']).default('availability'),
});
export const slotSchema = z.object({
    id: z.string(),
    teacherId: z.string(),
    teacherName: z.string(),
    subjectSlug: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    label: z.string(),
    durationMinutes: z.number(),
});
export const daySlotsSchema = z.object({
    date: isoDateSchema,
    weekdayShort: z.string(),
    dayOfMonth: z.number(),
    isToday: z.boolean(),
    slots: z.array(slotSchema),
});
export const availabilityQuerySchema = z.object({
    subject: optional(z.string()),
    teacherId: optional(z.string()),
    from: optional(isoDateSchema),
    days: z.coerce.number().int().min(1).max(28).default(7),
    type: z.nativeEnum(LessonType).default(LessonType.TRIAL),
});
export const availabilityResponseSchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    timezone: z.string(),
    days: z.array(daySlotsSchema),
    totalSlots: z.number(),
});
export const bookingContactSchema = z.object({
    fullName: z.string().trim().min(2, 'validation.nameRequired').max(120),
    email: emailSchema,
    phone: phoneSchema,
    notes: optional(z.string().trim().max(600, 'validation.notesTooLong')),
    experience: optional(z.nativeEnum(SkillLevel)),
    whatsappOptIn: z.boolean().default(true),
    locale: optional(localeSchema),
});
export const createLessonBookingSchema = z.object({
    slotId: z.string().min(1, 'validation.chooseTime'),
    subjectSlug: z.string().min(1, 'validation.chooseSubject'),
    teacherId: z.string().min(1, 'validation.chooseTeacher'),
    type: z.nativeEnum(LessonType).default(LessonType.TRIAL),
    contact: bookingContactSchema,
    requestId: z.string().uuid().optional(),
});
export const bookingSchema = z.object({
    id: z.string(),
    reference: z.string(),
    status: z.nativeEnum(BookingStatus),
    type: z.nativeEnum(LessonType),
    startsAt: z.string(),
    endsAt: z.string(),
    timezone: z.string(),
    teacher: z.object({
        id: z.string(),
        fullName: z.string(),
        slug: z.string(),
        photoUrl: z.string().nullable(),
    }),
    subject: z.object({ slug: z.string(), name: z.string() }),
    contactName: z.string(),
    contactEmail: z.string(),
    contactPhone: z.string(),
    notes: z.string().nullable(),
    priceMinor: z.number(),
    currency: z.string(),
    paymentStatus: z.nativeEnum(PaymentStatus),
    locationLine: z.string(),
    createdAt: z.string(),
});
export const roomSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    description: z.string(),
    longDescription: z.string(),
    capacity: z.number(),
    equipment: z.array(z.string()),
    equipmentIcon: z.string(),
    hourlyRateMinor: z.number(),
    currency: z.string(),
    photos: z.array(z.string()),
    houseRules: z.array(z.string()),
    availabilityState: z.nativeEnum(RoomAvailabilityState),
    availabilityLabel: z.string(),
    openFrom: z.string(),
    openTo: z.string(),
});
export const roomQuerySchema = z.object({
    equipment: z.string().optional(),
    capacity: z.coerce.number().int().min(1).max(20).optional(),
    date: isoDateSchema.optional(),
});
export const createRoomBookingSchema = z.object({
    roomId: z.string().min(1),
    slotIds: z.array(z.string().min(1)).min(1, 'validation.chooseHour').max(6),
    contact: bookingContactSchema,
    requestId: z.string().uuid().optional(),
});
export const roomBookingSchema = z.object({
    id: z.string(),
    reference: z.string(),
    status: z.nativeEnum(BookingStatus),
    room: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
    startsAt: z.string(),
    endsAt: z.string(),
    hours: z.number(),
    timezone: z.string(),
    contactName: z.string(),
    contactEmail: z.string(),
    contactPhone: z.string(),
    totalMinor: z.number(),
    currency: z.string(),
    paymentStatus: z.nativeEnum(PaymentStatus),
    createdAt: z.string(),
});
export const faqItemSchema = z.object({
    id: z.string(),
    question: z.string(),
    answer: z.string(),
    category: z.string(),
});
export const testimonialSchema = z.object({
    id: z.string(),
    quote: z.string(),
    authorName: z.string(),
    authorContext: z.string(),
    subjectSlug: z.string().nullable(),
    rating: z.number().nullable(),
});
export const communityPostSchema = z.object({
    id: z.string(),
    title: z.string(),
    excerpt: z.string(),
    authorName: z.string(),
    authorRole: z.nativeEnum(UserRole),
    publishedAt: z.string(),
    tag: z.string(),
});
export const contactMessageSchema = z.object({
    fullName: z.string().trim().min(2, 'validation.nameRequired').max(120),
    email: emailSchema,
    phone: optional(phoneSchema),
    topic: z.enum(['lessons', 'practice-rooms', 'careers', 'other']).default('lessons'),
    message: z.string().trim().min(10, 'validation.messageShort').max(2000),
    locale: optional(localeSchema),
});
export const scheduleEntrySchema = z.object({
    id: z.string(),
    reference: z.string(),
    kind: z.nativeEnum(ScheduleEntryKind),
    status: z.nativeEnum(BookingStatus),
    startsAt: z.string(),
    endsAt: z.string(),
    durationMinutes: z.number(),
    timezone: z.string(),
    studio: z.string().nullable(),
    cancelledByAcademy: z.boolean(),
    subject: z.object({ slug: z.string(), name: z.string(), icon: z.string() }).nullable(),
    teacher: z
        .object({
        id: z.string(),
        fullName: z.string(),
        slug: z.string(),
        photoUrl: z.string().nullable(),
    })
        .nullable(),
    room: z.object({ id: z.string(), name: z.string(), slug: z.string() }).nullable(),
});
export const scheduleQuerySchema = z.object({
    from: isoDateSchema.optional(),
    days: z.coerce.number().int().min(1).max(31).default(7),
    filter: z.nativeEnum(ScheduleFilter).default(ScheduleFilter.ALL),
});
export const scheduleDaySchema = z.object({
    date: isoDateSchema,
    weekday: z.string(),
    dayOfMonth: z.number(),
    isToday: z.boolean(),
    entries: z.array(scheduleEntrySchema),
});
export const scheduleResponseSchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    weekNumber: z.number(),
    timezone: z.string(),
    days: z.array(scheduleDaySchema),
    upcoming: z.array(scheduleEntrySchema),
    past: z.array(scheduleEntrySchema),
});
export const lessonDetailSchema = scheduleEntrySchema.extend({
    type: z.nativeEnum(LessonType),
    locationLine: z.string(),
    notes: z.string().nullable(),
    bookedAt: z.string(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    teacherSince: z.string().nullable(),
    periodAttended: z.number(),
    periodScheduled: z.number(),
    canCancel: z.boolean(),
    canReschedule: z.boolean(),
});
export const paymentPeriodSchema = z.object({
    id: z.string(),
    invoiceNo: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    description: z.string(),
    amountMinor: z.number(),
    currency: z.string(),
    status: z.nativeEnum(PaymentPeriodStatus),
    dueAt: z.string().nullable(),
    paidAt: z.string().nullable(),
    receiptUrl: z.string().nullable(),
    rateLabel: z.string(),
    lessonsScheduled: z.number(),
    lessonsAttended: z.number(),
    lessonsCancelled: z.number(),
    lessonsUpcoming: z.number(),
    adjustmentsMinor: z.number(),
});
export const paymentLineSchema = z.object({
    id: z.string(),
    date: z.string(),
    subjectName: z.string(),
    subjectSlug: z.string(),
    durationMinutes: z.number().int().positive(),
    teacherName: z.string(),
    status: z.nativeEnum(BookingStatus),
    cancelledByAcademy: z.boolean(),
    upcoming: z.boolean(),
});
export const paymentDetailSchema = paymentPeriodSchema.extend({
    lines: z.array(paymentLineSchema),
});
export const paymentsOverviewSchema = z.object({
    current: paymentPeriodSchema.nullable(),
    history: z.array(paymentPeriodSchema),
});
export const notificationSchema = z.object({
    id: z.string(),
    category: z.nativeEnum(NotificationCategory),
    title: z.string(),
    body: z.string(),
    href: z.string().nullable(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
export const notificationListSchema = z.object({
    items: z.array(notificationSchema),
    unread: z.number(),
});
const communityAuthorSchema = z.object({
    name: z.string(),
    role: z.nativeEnum(UserRole),
    context: z.string(),
    photoUrl: z.string().nullable(),
});
export const communityAnswerSchema = z.object({
    id: z.string(),
    author: communityAuthorSchema,
    body: z.string(),
    createdAt: z.string(),
    helpfulCount: z.number(),
    viewerFoundHelpful: z.boolean(),
    markedHelpful: z.boolean(),
});
export const communityThreadSchema = z.object({
    id: z.string(),
    slug: z.string(),
    topic: z.nativeEnum(CommunityTopic),
    title: z.string(),
    body: z.string(),
    excerpt: z.string(),
    author: communityAuthorSchema,
    createdAt: z.string(),
    answerCount: z.number(),
    helpfulCount: z.number(),
    viewerFoundHelpful: z.boolean(),
    viewerSaved: z.boolean(),
});
export const communityThreadDetailSchema = communityThreadSchema.extend({
    answers: z.array(communityAnswerSchema),
    related: z.array(z.object({ slug: z.string(), title: z.string() })),
});
export const communityFeedSchema = z.object({
    threads: z.array(communityThreadSchema),
    popularTopics: z.array(z.object({ topic: z.nativeEnum(CommunityTopic), count: z.number() })),
});
export const communityQuerySchema = z.object({
    topic: optional(z.nativeEnum(CommunityTopic)),
    q: optional(z.string().trim().max(120)),
});
export const createCommunityQuestionSchema = z.object({
    title: z.string().trim().min(12, 'validation.questionShort').max(180, 'validation.questionLong'),
    body: optional(z.string().trim().max(4000)),
    topic: z.nativeEnum(CommunityTopic),
    subjectSlug: optional(z.string().trim().max(60)),
});
export const createCommunityAnswerSchema = z.object({
    body: z.string().trim().min(20, 'validation.answerShort').max(4000),
});
export const notificationPreferencesSchema = z.object({
    lessonReminders: z.boolean(),
    bookingConfirmations: z.boolean(),
    paymentReminders: z.boolean(),
    scheduleChanges: z.boolean(),
    communityReplies: z.boolean(),
    academyNews: z.boolean(),
});
export const updateProfileSchema = z.object({
    fullName: optional(z.string().trim().min(2, 'validation.nameRequired').max(120)),
    phone: optional(phoneSchema),
    locale: optional(localeSchema),
    avatarUrl: z.string().url().max(500).nullable().optional(),
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, 'validation.currentPasswordRequired'),
    password: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((value) => value.password === value.confirmPassword, {
    path: ['confirmPassword'],
    message: 'validation.passwordsMatch',
});
export const studentSubjectSchema = z.object({
    subject: z.object({ slug: z.string(), name: z.string(), icon: z.string() }),
    teacherName: z.string().nullable(),
    since: z.string().nullable(),
    trial: z.boolean(),
});
export const studentProfileSchema = z.object({
    user: authUserSchema,
    preferences: notificationPreferencesSchema,
    subjects: z.array(studentSubjectSchema),
    passwordChangedAt: z.string().nullable(),
});
export const dashboardSchema = z.object({
    firstName: z.string(),
    now: z.string(),
    lessonsThisWeek: z.number(),
    roomsThisWeek: z.number(),
    next: lessonDetailSchema.nullable(),
    payment: paymentPeriodSchema.nullable(),
    week: z.array(scheduleEntrySchema),
    community: z.array(communityThreadSchema),
    unreadNotifications: z.number(),
    suggestedSubjects: z.array(z.string()),
});
export const studentRefSchema = z.object({
    id: z.string().nullable(),
    fullName: z.string(),
    shortName: z.string(),
    photoUrl: z.string().nullable(),
});
export const teacherBookingSchema = z.object({
    id: z.string(),
    reference: z.string(),
    state: z.nativeEnum(SlotState),
    type: z.nativeEnum(LessonType),
    status: z.nativeEnum(BookingStatus),
    mark: z.nativeEnum(AttendanceMark),
    startsAt: z.string(),
    endsAt: z.string(),
    startMinute: z.number(),
    endMinute: z.number(),
    durationMinutes: z.number(),
    student: studentRefSchema.nullable(),
    subject: z.object({ slug: z.string(), name: z.string(), icon: z.string() }).nullable(),
    studio: z.string().nullable(),
    cancelledByAcademy: z.boolean(),
});
export const workingWindowSchema = z.object({
    startMinute: z.number(),
    endMinute: z.number(),
});
export const teacherWeekDaySchema = z.object({
    date: isoDateSchema,
    weekday: z.string(),
    dayOfMonth: z.number(),
    isToday: z.boolean(),
    workingWindows: z.array(workingWindowSchema),
    lessonCount: z.number(),
    bookings: z.array(teacherBookingSchema),
});
export const teacherWeekSchema = z.object({
    teacherId: z.string(),
    from: isoDateSchema,
    to: isoDateSchema,
    weekNumber: z.number(),
    timezone: z.string(),
    gridStartMinute: z.number(),
    gridEndMinute: z.number(),
    gridStepMinutes: z.number(),
    now: z.string(),
    days: z.array(teacherWeekDaySchema),
});
export const teacherWeekQuerySchema = z.object({
    from: isoDateSchema.optional(),
});
export const registerLessonSchema = z.object({
    id: z.string(),
    reference: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    durationMinutes: z.number(),
    type: z.nativeEnum(LessonType),
    mark: z.nativeEnum(AttendanceMark),
    student: studentRefSchema.nullable(),
    subject: z.object({ slug: z.string(), name: z.string(), icon: z.string() }).nullable(),
    studio: z.string().nullable(),
});
export const registerDaySchema = z.object({
    date: isoDateSchema,
    isToday: z.boolean(),
    overdue: z.boolean(),
    total: z.number(),
    marked: z.number(),
    remaining: z.number(),
    lessons: z.array(registerLessonSchema),
});
export const attendanceRegisterSchema = z.object({
    range: z.nativeEnum(AttendanceRange),
    date: isoDateSchema,
    now: z.string(),
    total: z.number(),
    marked: z.number(),
    remaining: z.number(),
    days: z.array(registerDaySchema),
});
export const attendanceQuerySchema = z.object({
    range: z.nativeEnum(AttendanceRange).default(AttendanceRange.TODAY),
    date: isoDateSchema.optional(),
});
export const markAttendanceSchema = z.object({
    mark: z.nativeEnum(AttendanceMark),
});
export const markDayPresentSchema = z.object({
    date: isoDateSchema,
});
export const attendanceResultSchema = z.object({
    lesson: registerLessonSchema,
    previousMark: z.nativeEnum(AttendanceMark),
    day: z.object({
        date: isoDateSchema,
        total: z.number(),
        marked: z.number(),
        remaining: z.number(),
    }),
});
export const attendanceHistoryEntrySchema = z.object({
    date: z.string(),
    mark: z.nativeEnum(AttendanceMark),
});
export const teacherLessonDetailSchema = teacherBookingSchema.extend({
    locationLine: z.string(),
    bookedAt: z.string(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    studentNotes: z.string().nullable(),
    teacherNotes: z.string().nullable(),
    level: z.nativeEnum(SkillLevel).nullable(),
    studentSince: z.string().nullable(),
    recentMarks: z.array(attendanceHistoryEntrySchema),
    periodAttended: z.number(),
    periodScheduled: z.number(),
    canCancel: z.boolean(),
    contactPhone: z.string().nullable(),
});
export const teacherLessonNotesSchema = z.object({
    teacherNotes: z.string().trim().max(4000, 'validation.tooLong'),
});
export const cancelLessonSchema = z.object({
    reason: optional(z.string().trim().max(500)),
});
export const teacherStudentSchema = studentRefSchema.extend({
    subject: z.object({ slug: z.string(), name: z.string(), icon: z.string() }).nullable(),
    level: z.nativeEnum(SkillLevel).nullable(),
    since: z.string().nullable(),
    trial: z.boolean(),
    attendedCount: z.number(),
    scheduledCount: z.number(),
    missedCount: z.number(),
    standing: z.nativeEnum(StudentStanding),
    nextLessonAt: z.string().nullable(),
    nextLessonReference: z.string().nullable(),
});
export const teacherStudentsSchema = z.object({
    students: z.array(teacherStudentSchema),
    activeCount: z.number(),
    subjects: z.array(z.object({ slug: z.string(), name: z.string() })),
});
export const teacherStudentsQuerySchema = z.object({
    scope: z.nativeEnum(StudentScope).default(StudentScope.ALL),
    subject: optional(z.string().trim().max(60)),
    q: optional(z.string().trim().max(120)),
});
export const upcomingTrialSchema = z.object({
    reference: z.string(),
    startsAt: z.string(),
    bookedAt: z.string(),
    student: studentRefSchema.nullable(),
    level: z.nativeEnum(SkillLevel).nullable(),
    firstTime: z.boolean(),
});
export const scheduleChangeSchema = z.object({
    kind: z.enum(['CANCELLED', 'NEW']),
    at: z.string(),
    reference: z.string(),
    startsAt: z.string(),
    type: z.nativeEnum(LessonType),
    student: studentRefSchema.nullable(),
    cancelledByAcademy: z.boolean(),
});
export const teacherDashboardSchema = z.object({
    firstName: z.string(),
    now: z.string(),
    lessonsToday: z.number(),
    trialsThisWeek: z.number(),
    needAttendance: z.number(),
    activeStudents: z.number(),
    nextLessonAt: z.string().nullable(),
    today: registerDaySchema,
    pending: z.array(registerDaySchema),
    upcomingTrials: z.array(upcomingTrialSchema),
    changes: z.array(scheduleChangeSchema),
    questions: z.array(z.object({ slug: z.string(), title: z.string() })),
    questionsSubject: z.object({ slug: z.string(), name: z.string() }).nullable(),
    unreadNotifications: z.number(),
});
export const teacherCommunityActivitySchema = z.object({
    answersThisYear: z.number(),
    markedHelpful: z.number(),
    waitingOnYou: z.number(),
});
export const teacherCommunityFeedSchema = communityFeedSchema.extend({
    activity: teacherCommunityActivitySchema,
});
export const teacherCommunityQuerySchema = z.object({
    scope: z.nativeEnum(CommunityScope).default(CommunityScope.UNANSWERED),
    topic: optional(z.nativeEnum(CommunityTopic)),
    subject: optional(z.string().trim().max(60)),
    q: optional(z.string().trim().max(120)),
});
export const workingHoursDaySchema = z.object({
    dayOfWeek: z.number().int().min(0).max(6),
    enabled: z.boolean(),
    startMinute: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .nullable(),
    endMinute: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .nullable(),
});
export const workingHoursSchema = z.object({
    days: z.array(workingHoursDaySchema).length(7),
});
export const updateWorkingHoursSchema = workingHoursSchema.superRefine((value, ctx) => {
    const seen = new Set();
    value.days.forEach((day, index) => {
        if (seen.has(day.dayOfWeek)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['days', index, 'dayOfWeek'],
                message: 'validation.weekdayOnce',
            });
        }
        seen.add(day.dayOfWeek);
        if (!day.enabled)
            return;
        if (day.startMinute === null || day.endMinute === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['days', index, 'startMinute'],
                message: 'validation.dayNeedsHours',
            });
            return;
        }
        if (day.endMinute <= day.startMinute) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['days', index, 'endMinute'],
                message: 'validation.dayEndAfterStart',
            });
        }
    });
});
export const teacherNotificationPreferencesSchema = z.object({
    newBookings: z.boolean(),
    cancellations: z.boolean(),
    attendanceReminders: z.boolean(),
    communityReplies: z.boolean(),
});
export const teacherProfileSchema = z.object({
    user: authUserSchema,
    slug: z.string().nullable(),
    displayName: z.string(),
    headline: z.string(),
    bio: z.string(),
    subjects: z.array(z.object({ slug: z.string(), name: z.string(), icon: z.string() })),
    availableSubjects: z.array(z.object({ slug: z.string(), name: z.string() })),
    workingHours: workingHoursSchema,
    preferences: teacherNotificationPreferencesSchema,
    passwordChangedAt: z.string().nullable(),
});
export const updateTeacherProfileSchema = z.object({
    displayName: optional(z.string().trim().min(2, 'validation.nameRequired').max(120)),
    bio: optional(z.string().trim().max(2000)),
    subjectSlugs: z.array(z.string().trim().max(60)).max(12).optional(),
});
export const ApiErrorCode = {
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    UNAUTHORISED: 'UNAUTHORISED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    SLOT_TAKEN: 'SLOT_TAKEN',
    SLOT_EXPIRED: 'SLOT_EXPIRED',
    NO_AVAILABILITY: 'NO_AVAILABILITY',
    DUPLICATE_BOOKING: 'DUPLICATE_BOOKING',
    RATE_LIMITED: 'RATE_LIMITED',
    EMAIL_TAKEN: 'EMAIL_TAKEN',
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    TOKEN_INVALID: 'TOKEN_INVALID',
    ACCOUNT_PENDING: 'ACCOUNT_PENDING',
    ACCOUNT_REJECTED: 'ACCOUNT_REJECTED',
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    FILE_TYPE_UNSUPPORTED: 'FILE_TYPE_UNSUPPORTED',
    IMAGE_DIMENSIONS: 'IMAGE_DIMENSIONS',
    INTERNAL: 'INTERNAL',
};
export const apiErrorSchema = z.object({
    statusCode: z.number(),
    code: z.nativeEnum(ApiErrorCode),
    message: z.string(),
    fieldErrors: z.record(z.string(), z.string()).optional(),
});
export const academyDetailsSchema = z.object({
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    openMinute: z.number().int(),
    closeMinute: z.number().int(),
    closedWeekdays: z.array(z.number().int()),
    legalDraft: z.boolean(),
});
//# sourceMappingURL=schemas.js.map